import type { PropsWithChildren } from 'react';
import type { DateTimeValue } from '@elfsight/configurator-contracts';
import { useSettings } from '@wu/widget-core';
import {
  endOfDay,
  getDay,
  getTime,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;
type Time = {
  h?: number | string;
  m?: number | string;
  s?: number | string;
  ms?: number | string;
};

const getTimeValues = (setting: string | DateTimeValue<'time'>) => {
  let values = ['0', '0'];

  if (!setting) return values;

  if (typeof setting === 'string') values = setting.split(':');

  if (typeof setting === 'object') values = setting.time.split(':');

  return values.map((value) => Number(value));
};

const getStamp = (date: Date, { h = 0, m = 0, s = 0, ms = 0 }: Time) => {
  let result = new Date(date);
  result = setHours(result, Number(h));
  result = setMinutes(result, Number(m));
  result = setSeconds(result, Number(s));
  result = setMilliseconds(result, Number(ms));
  return result.getTime();
};

export const FilterTime = ({ children }: PropsWithChildren) => {
  const {
    displayDays,
    timeSchedule,
    displayStartTime,
    displayEndTime,
    timeZone,
    editMode
  } = useSettings();

  // Получаем текущий день недели
  const currentDay = new Date();
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = utcToZonedTime(currentDay, tz);

  let showChat = displayDays?.includes(getDay(now));

  if (timeSchedule) {
    const [startHour, startMinute] = getTimeValues(displayStartTime);
    const [endHour, endMinute] = getTimeValues(displayEndTime);

    const endTimeNotExceedStartTime =
      endHour < startHour ||
      (endHour === startHour && endMinute <= startMinute);

    const currentStamp = getTime(now);
    const stampWithStartTime = getStamp(now, { h: startHour, m: startMinute });
    const stampWithEndTime = getStamp(now, { h: endHour, m: endMinute });
    const stampOfTodayStart = getStamp(startOfDay(now), { h: 0, m: 0 });
    const stampOfTodayEnd = getStamp(endOfDay(now), {
      h: 23,
      m: 59,
      s: 59,
      ms: 999
    });

    if (endTimeNotExceedStartTime) {
      const prevDayOfWeek = (getDay(now) + 6) % 7;

      showChat =
        (displayDays?.includes(getDay(now)) &&
          currentStamp >= stampWithStartTime &&
          currentStamp <= stampOfTodayEnd) ||
        (displayDays?.includes(prevDayOfWeek as WeekDay) &&
          currentStamp >= stampOfTodayStart &&
          currentStamp <= stampWithEndTime);
    } else {
      showChat =
        displayDays?.includes(getDay(now)) &&
        currentStamp >= stampWithStartTime &&
        currentStamp <= stampWithEndTime;
    }
  }

  if (editMode || showChat) {
    return children;
  }

  return null;
};
